import { computed, ref, Ref } from '@vue/composition-api';
import { isEmpty, debounce } from 'lodash-es';
import uuidv4 from 'uuid-v4';
import { useTrackTitleSuggestion } from './use-track-title-suggestion';
import {
  useGetSmartTitleSuggestionApi,
  useRequest,
  useAdvInfoApi,
  useStore,
  StateEnum,
  CancelToken,
  useStdCreativeAdgroupIs
} from '@tt4b/ttam-hooks';
import { AcoFormData, AdgroupFormData, CampaignFormData, CREATIVE_TYPE, FullNormalFormData } from '@tt4b/ttam-enums';

const showStatusList = ref<boolean[]>([]); // popover显示状态
const currentTitleIndex = ref(0); // 当前选中的title下标
const isLoading = ref(false); // 是否正在加载中
const queryIdList: string[] = []; // 请求标题建议的id列表，一个文本框有一个请求标识
const titleSuggestionInfo = ref<{ requestId?: string; titles?: { title: string; matchKeywords: string[] }[] }>({});

// eslint-disable-next-line max-lines-per-function
export function useTitleSuggestion(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  creativeData: Ref<FullNormalFormData & AcoFormData>;
}) {
  const { adgroupData, campaignData, creativeData } = opt;
  const { state } = useStore();
  const accountInfo = computed(() => state[StateEnum.accountInfo].data.value);
  const aadvid = computed(() => state[StateEnum.baseInfo].value.aadvid);
  const accountCountry = computed(() => accountInfo.value.account.country);
  const { data: advertiserInfo, run: fetchAdvertiserInfo } = useRequest(useAdvInfoApi);
  const { isAco } = useStdCreativeAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { addRequestInfo, trackSelectSuggestion } = useTrackTitleSuggestion({ adgroupData, creativeData });

  const isShow = computed(() => showStatusList.value[currentTitleIndex.value]);
  let cancelTokenObj = '' as any;

  /**
   * 更新指定下标的表单数据
   */
  const updateShowStatus = (index, value) => {
    const showList = [...showStatusList.value];
    showList[index] = value;
    showStatusList.value = showList;
  };

  /**
   * 由于法务功能，广告主注册地为 美国 or 加拿大 的，则不支持该功能
   */
  const isSupportCountry = computed(() => !['US', 'CA'].includes(accountCountry.value));

  // 0.8s内返回，则不需要loading状态
  const NOT_LOADING_WAIT_TIME = 800;
  /**
   * 获取当前标题的建议
   * @param userTitle 当前标题填写的内容
   * @param titleIndex 当前标题下标（兼容ACO多个标题的场景）
   */
  const getTitleSuggestionList = async (userTitle, titleIndex = 0) => {
    if (!isSupportCountry.value) {
      return;
    }

    let timer;
    try {
      timer = setTimeout(() => {
        isLoading.value = true;
      }, NOT_LOADING_WAIT_TIME);

      if (cancelTokenObj) {
        cancelTokenObj.cancel();
      }
      cancelTokenObj = CancelToken.source();
      currentTitleIndex.value = titleIndex;

      // 获取账号的行业信息
      await fetchAdvertiserInfo();

      if (!queryIdList[titleIndex]) {
        queryIdList[titleIndex] = uuidv4();
      }

      // 获取标题建议的参数
      const param: Record<string, unknown> = {
        origin: 'tiktok_ads',
        page_size: 5,
        adv_id: aadvid.value,
        ad_id: adgroupData.value.adId || '',
        industry: Number(advertiserInfo.value?.secondIndustry),
        support_countries: adgroupData.value.country?.map(o => String(o)), // 投放国家的列表
        prefix_title: userTitle, // 用户已经输入的部分标题
        query_id: queryIdList[titleIndex],
      };

      console.log('param', param);

      if (isAco.value) {
        // ACO需要把已经选中的标题传过去，这里过滤了正在编辑的那一项
        const originTitleList = creativeData.value.titleList || [];
        const titleList = originTitleList.map(item => item.title);
        titleList.splice(titleIndex, 1);
        param.his_suggestions = titleList;
      }
      const result = await useGetSmartTitleSuggestionApi(param, cancelTokenObj);
      titleSuggestionInfo.value = result || {};

      updateShowStatus(titleIndex, !isEmpty(titleSuggestionInfo.value.titles));
      // 记录请求信息，用户埋点
      addRequestInfo(result);
    } catch (e) {
      console.log(e);
      titleSuggestionInfo.value = {};
    } finally {
      // 接口0.8内返回，则取消定时器
      timer && clearTimeout(timer);
      if (isLoading.value) {
        isLoading.value = false;
      }
    }
  };

  // 输入标题防抖的时间，先暂定1s，联调看效果修改时间
  const WAIT_TIME = 1000;
  const debounceGetTitleSuggestion = debounce(getTitleSuggestionList, WAIT_TIME);
  const immediateDebounceGetTitleSuggestion = debounce(getTitleSuggestionList, WAIT_TIME, {
    leading: true,
  });

  const handleCancelDebounceRequest = () => {
    try {
      // 失去焦点，函数防抖还没有执行，则取消执行
      if (debounceGetTitleSuggestion && debounceGetTitleSuggestion.cancel) {
        debounceGetTitleSuggestion.cancel();
      }
      // 失去焦点，还有请求未返回，则取消请求
      if (cancelTokenObj) {
        cancelTokenObj.cancel();
      }
    } catch (error) {}
  };

  /**
   * 隐藏标题建议下拉框
   * @param isBlur 失去焦点的隐藏，则需要埋点上报
   */
  const hide = (isBlur = false) => {
    isLoading.value = false;
    updateShowStatus(currentTitleIndex.value, false);
    if (isBlur) {
      handleCancelDebounceRequest();
      trackSelectSuggestion({ isBlur });
    }
  };

  return {
    isShow,
    currentTitleIndex,
    isLoading,
    titleSuggestionInfo,
    hide,
    getTitleSuggestionList,
    debounceGetTitleSuggestion,
    immediateDebounceGetTitleSuggestion,
  };
}
